import { z } from 'zod'

import type { FlowLevel, Horizon, Metric, MoverMetric } from '@/client'
import { pruned } from '@/lib/filters'
import { dashboardInfo, type DashboardCardId } from '@/i18n/locales/en/dashboardInfo'
import { KPI_METRICS } from '@/lib/metricSeries'

/**
 * The dashboard's URL: the Sales & marketing filters (the window, places,
 * brands and the currency money is shown in), then every control that
 * changes what a section shows — the figure picked in the KPI row, the main
 * chart's view and grain, the forecast's horizon, the flow's steps in their
 * order, each list's ranking, the ticket month and week, and the list open in
 * a help panel. Optional throughout, defaults applied at read time, so `/` is
 * the default view.
 */

const ids = z.array(z.number().int()).max(500).optional().catch(undefined)
const currency = z
  .string()
  .regex(/^[A-Z]{3}$/)
  .optional()
  .catch(undefined)
const day = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined)

/** The dashboard endpoints' figures: what a flow, forecast and growth series can be drawn for. */
export const METRICS = [
  'total_sales',
  'total_returns',
  'ams_sales',
  'ams_spend',
  'roas',
] as const satisfies readonly Metric[]

export const FLOW_LEVELS = [
  'category',
  'marketplace',
  'store',
  'brand',
  'product_category',
] as const satisfies readonly FlowLevel[]

/** The steps a metric can be split by, as the backend allows them. */
export const LEVELS_OF: Record<Metric, readonly FlowLevel[]> = {
  total_sales: FLOW_LEVELS,
  total_returns: ['marketplace', 'store', 'brand', 'product_category'],
  ams_sales: ['marketplace', 'store', 'brand'],
  ams_spend: ['marketplace', 'store', 'brand'],
  roas: [],
}

export const DEFAULT_LEVELS: readonly FlowLevel[] = ['category', 'marketplace', 'store']

export const MOVER_METRICS = [
  'rank',
  'sales',
  'ams_spend',
  'ams_clicks',
  'ams_sales',
  'acos',
] as const satisfies readonly MoverMetric[]

/** The Performance view's groupings, as the report APIs name them. */
export const PERFORMANCE_DIMENSIONS = [
  'country',
  'family',
  'brand',
  'category',
  'subcategory',
] as const
export type PerformanceDimension = (typeof PERFORMANCE_DIMENSIONS)[number]

/** What the main chart shows: the picked figure with its forecast, its growth month on month, or its groups ranked. */
export const VIEWS = ['forecast', 'growth', 'groups'] as const
export type View = (typeof VIEWS)[number]

/** The dashboard's tables, each with its own order and column filters in the URL. */
export const TABLE_IDS = [
  'movers',
  'noSale',
  'cover',
  'suppressed',
  'slots',
  'distressed',
  'worklist',
  'threads',
  'groups',
  'countries',
  'adCountries',
] as const
export type TableId = (typeof TABLE_IDS)[number]

/** A table's view: the column it is sorted by and which way, and its header funnels as `column:op:value`. */
const tableView = z
  .object({
    sort: z.string().max(60).optional().catch(undefined),
    order: z.enum(['asc', 'desc']).optional().catch(undefined),
    filters: z.array(z.string().max(120)).max(20).optional().catch(undefined),
  })
  .catch({})
export type TableView = z.infer<typeof tableView>

/** The cards whose help panel can be open: the copy's own list. */
const CARD_IDS = Object.keys(dashboardInfo.cards) as [DashboardCardId, ...DashboardCardId[]]

export const dashboardSearchSchema = z.object({
  dateFrom: day.describe(
    'First day of the Sales & marketing period, YYYY-MM-DD; the last 30 days when absent.',
  ),
  dateTo: day.describe(
    'Last day of the Sales & marketing period, YYYY-MM-DD, inclusive; today when absent.',
  ),
  // Stores, by id. A marketplace is picked as all of its stores, in the same list.
  storeIds: ids.describe(
    'Stores the Sales & marketing section counts, by id; a marketplace is picked as all of its stores. Every store when absent.',
  ),
  brandIds: ids.describe(
    'Brands the Sales & marketing section counts, by id; every brand when absent.',
  ),
  // SKUs, by code: Sales & marketing's figures for these only.
  skus: z
    .array(z.string().min(1).max(64))
    .max(50)
    .optional()
    .catch(undefined)
    .describe(
      "SKU codes (up to 50) Sales & marketing's figures are narrowed to; every SKU when absent.",
    ),
  // The currency each section's amounts are shown in; the organisation's own
  // when absent. Sales & marketing, Focused SKUs and Advertising each have one.
  cur: currency.describe(
    "Three-letter currency Sales & marketing's amounts are converted to; the organisation's reporting currency when absent.",
  ),
  focusedCur: currency.describe(
    "Three-letter currency the Focused SKUs section's amounts are shown in; the organisation's reporting currency when absent.",
  ),
  adsCur: currency.describe(
    "Three-letter currency the Advertising section's amounts are shown in; the organisation's reporting currency when absent.",
  ),
  // The KPI card picked: the figure the main chart, the flow and growth draw.
  metric: z
    .enum(KPI_METRICS)
    .optional()
    .catch(undefined)
    .describe(
      'The headline figure picked in the KPI row, which the main chart draws; sales by default.',
    ),
  view: z
    .enum(VIEWS)
    .optional()
    .catch(undefined)
    .describe(
      'What the main chart shows: forecast, month-on-month growth or ranked groups; forecast by default.',
    ),
  horizon: z
    .enum(['week', 'month'] as const satisfies readonly Horizon[])
    .optional()
    .catch(undefined)
    .describe('How far the forecast runs: to the end of the week or of the month.'),
  levels: z
    .array(z.enum(FLOW_LEVELS))
    .max(5)
    .optional()
    .catch(undefined)
    .describe(
      "The flow chart's steps, in order; category, marketplace, store by default, kept to those the figure allows.",
    ),
  mover: z
    .enum(MOVER_METRICS)
    .optional()
    .catch(undefined)
    .describe('What the Focused SKUs lists rank by; Amazon rank by default.'),
  tMonth: z
    .string()
    .regex(/^\d{4}-\d{2}$/)
    .optional()
    .catch(undefined)
    .describe('The month the Tickets section counts, YYYY-MM; this month by default.'),
  tWeek: z
    .number()
    .int()
    .min(1)
    .max(5)
    .optional()
    .catch(undefined)
    .describe(
      'The week of that month the Tickets section narrows to, 1–5; the whole month when absent.',
    ),
  tDone: z
    .enum(['solved', 'reviewed', 'assigned'])
    .optional()
    .catch(undefined)
    .describe('Which finished tickets the "Done" chart counts; solved by default.'),
  // The sales map's measure.
  mapBy: z
    .enum(['orders', 'units', 'sales', 'spend', 'adSales', 'clicks'])
    .optional()
    .catch(undefined)
    .describe('The measure the country card maps; follows the KPI picked when absent.'),
  // The country card: sales or advertising; absent, it follows the KPI picked.
  geo: z
    .enum(['sales', 'ads'])
    .optional()
    .catch(undefined)
    .describe(
      'Whether the country card shows sales or advertising; follows the KPI picked when absent.',
    ),
  // The forum's thread order and the contributors' month.
  forumSort: z
    .enum(['created', 'comments', 'likes'])
    .optional()
    .catch(undefined)
    .describe('The order of the Community forum threads; newest first by default.'),
  forumMonth: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
    .optional()
    .catch(undefined)
    .describe(
      'The month the forum\'s "Most contributed" list counts, YYYY-MM; this month by default.',
    ),
  // The main chart's grouping (its ranked view and its table) and grain.
  by: z
    .enum(PERFORMANCE_DIMENSIONS)
    .optional()
    .catch(undefined)
    .describe("What the main chart's ranked view and its table group by; country by default."),
  // Each table's order and column filters.
  tables: z
    .record(z.enum(TABLE_IDS), tableView)
    .optional()
    .catch(undefined)
    .describe(
      "Each dashboard table's sort column, direction and column filters, keyed by table id.",
    ),
  // The card whose help panel is open, so "how is this worked out?" is a link.
  about: z
    .enum(CARD_IDS)
    .optional()
    .catch(undefined)
    .describe('The card whose "how is this worked out" help panel is open.'),
  grain: z
    .enum(['day', 'week', 'month'])
    .optional()
    .catch(undefined)
    .describe('The period each point of the trend covers; day by default.'),
})

export type DashboardSearch = z.infer<typeof dashboardSearchSchema>

export const EMPTY_DASHBOARD_SEARCH: DashboardSearch = dashboardSearchSchema.parse({})

/** Strip defaults and empties so a clean URL stays clean. */
export function withoutDashboardDefaults(search: DashboardSearch): DashboardSearch {
  const levels =
    search.levels && search.levels.join() !== DEFAULT_LEVELS.join() ? search.levels : undefined
  const tables = Object.fromEntries(
    Object.entries(search.tables ?? {}).filter(
      ([, view]) => view && (view.sort || view.filters?.length),
    ),
  )
  return pruned({
    ...search,
    tables: Object.keys(tables).length ? tables : undefined,
    metric: search.metric === 'sales' ? undefined : search.metric,
    view: search.view === 'forecast' ? undefined : search.view,
    levels,
    mover: search.mover === 'rank' ? undefined : search.mover,
    tDone: search.tDone === 'solved' ? undefined : search.tDone,
    // The card's measure has no fixed default: it follows the KPI, so it is never stripped.
    mapBy: search.mapBy,
    forumSort: search.forumSort === 'created' ? undefined : search.forumSort,
    by: search.by === 'country' ? undefined : search.by,
    grain: search.grain === 'day' ? undefined : search.grain,
    storeIds: search.storeIds?.length ? search.storeIds : undefined,
    skus: search.skus?.length ? search.skus : undefined,
    brandIds: search.brandIds?.length ? search.brandIds : undefined,
  })
}

/** The flow's steps for a metric: the URL's, kept to those the metric allows. */
export function levelsFor(metric: Metric, levels: readonly FlowLevel[] | undefined): FlowLevel[] {
  const allowed = LEVELS_OF[metric]
  const picked = (levels ?? DEFAULT_LEVELS).filter((level) => allowed.includes(level))
  return picked.length ? picked : allowed.filter((level) => DEFAULT_LEVELS.includes(level))
}
